import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import DragIndicator from '@mui/icons-material/DragIndicator';
import Search from '@mui/icons-material/Search';
import { Draggable, Droppable, type DraggableProvided } from '@hello-pangea/dnd';
import { FixedSizeList, type ListChildComponentProps } from 'react-window';
import { BuilderPanel } from './StackBuilderLayout';
import { LIBRARY_DROPPABLE_ID } from './reorder';
import EntityLink from './EntityLink';
import { HOVER_ACTIONS, hoverActionsSx, revealHoverActions } from './hoverActions';
import OfferTypeFilter, { matchesTypes } from './OfferTypeFilter';
import {
  displayId,
  entityLinkFor,
  LIBRARY_KINDS,
  libraryKindOf,
  matchesSearch,
  typeLabel,
  type LibraryKind,
  type OfferLike,
} from './offerKinds';

const ROW_HEIGHT = 58;
export const libraryDraggableId = (offerId: number) => `library-${offerId}`;

export interface OfferLibraryProps<T extends OfferLike> {
  offers: T[];
  loading?: boolean;
  /** The kinds offered as filter chips; one kind hides the chips. */
  kinds?: LibraryKind[];
  /** Appends the offer to the end of the stack. */
  onAdd: (offer: T) => void;
  /** Called with what is currently listed, so a drop can look up the dragged offer by index. */
  onListedChange?: (listed: T[]) => void;
}

function LibraryRow({
  offer,
  provided,
  onAdd,
  dragging,
}: {
  offer: OfferLike;
  provided: DraggableProvided;
  onAdd?: () => void;
  dragging?: boolean;
}) {
  const { label, id } = displayId(offer);
  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      aria-label={`${offer.name}. Press space to pick it up, then the arrows and space to drop it into the stack.`}
      style={provided.draggableProps.style}
      sx={{
        position: 'relative',
        height: ROW_HEIGHT - 6,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 0.5,
        pr: 0.5,
        bgcolor: 'background.default',
        border: 1,
        borderColor: dragging ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        boxShadow: dragging ? 6 : 'none',
        cursor: 'grab',
        '&:hover': { borderColor: 'text.disabled' },
        ...revealHoverActions,
      }}
    >
      <DragIndicator fontSize='small' sx={{ color: 'text.disabled', flex: '0 0 auto' }} />
      <EntityLink {...entityLinkFor(offer)} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant='body2' noWrap title={offer.name} sx={{ fontWeight: 500 }}>
          {offer.name}
        </Typography>
        <Stack direction='row' spacing={0.75} sx={{ alignItems: 'center', color: 'text.secondary' }}>
          <Typography variant='caption'>{`${label} ${id}`}</Typography>
          <Chip label={typeLabel(offer)} size='small' sx={{ height: 18, fontSize: '0.6875rem' }} />
        </Stack>
      </Box>
      {onAdd && (
        <Box className={HOVER_ACTIONS} sx={hoverActionsSx}>
          <Tooltip title='Add to the end'>
            <IconButton size='small' aria-label={`Add ${offer.name} to the end`} onClick={onAdd}>
              <Add fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

/**
 * The column of offers to build from: search, kind chips, and a virtual list whose rows drag into
 * the stack (or append with +). Dragging copies — the library never loses a row, and dropping back
 * onto it does nothing.
 */
export default function OfferLibrary<T extends OfferLike>({
  offers,
  loading = false,
  kinds = LIBRARY_KINDS.map((k) => k.value),
  onAdd,
  onListedChange,
}: OfferLibraryProps<T>) {
  const [query, setQuery] = React.useState('');
  const deferredQuery = React.useDeferredValue(query);
  const [kind, setKind] = React.useState<LibraryKind>(kinds[0] ?? 'offers');
  const [types, setTypes] = React.useState<string[]>([]);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = React.useState(0);
  const listRef = React.useRef<FixedSizeList | null>(null);

  const counts = React.useMemo(() => {
    const result: Partial<Record<LibraryKind, number>> = {};
    offers.forEach((offer) => {
      const k = libraryKindOf(offer);
      result[k] = (result[k] ?? 0) + 1;
    });
    return result;
  }, [offers]);

  const ofKind = React.useMemo(
    () => (kinds.length < 2 ? offers : offers.filter((offer) => libraryKindOf(offer) === kind)),
    [offers, kind, kinds.length]
  );
  const listed = React.useMemo(
    () => ofKind.filter((offer) => matchesTypes(offer, types) && matchesSearch(offer, deferredQuery)),
    [ofKind, types, deferredQuery]
  );

  React.useEffect(() => onListedChange?.(listed), [listed, onListedChange]);
  // A new filter starts at the top rather than part-way down a list the user has not seen.
  React.useEffect(() => listRef.current?.scrollTo(0), [kind, types, deferredQuery]);

  // react-window needs a pixel height; the column's is whatever the grid gives it.
  React.useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    const measure = () => setHeight(body.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const Row = React.useCallback(
    ({ index, style, data }: ListChildComponentProps<T[]>) => {
      const offer = data[index];
      if (!offer) return null;
      return (
        <Box style={style}>
          <Draggable draggableId={libraryDraggableId(offer.id)} index={index}>
            {(provided) => <LibraryRow offer={offer} provided={provided} onAdd={() => onAdd(offer)} />}
          </Draggable>
        </Box>
      );
    },
    [onAdd]
  );

  return (
    <BuilderPanel
      label='Offer library'
      title='Add offers'
      aside={loading ? undefined : `${offers.length.toLocaleString()} available`}
      toolbar={
        <Stack spacing={1}>
          {/* Wraps the type filter under the search when the column is too narrow for both. */}
          <Stack direction='row' useFlexGap spacing={1} sx={{ flexWrap: 'wrap' }}>
            <TextField
              size='small'
              sx={{ flex: '1 1 180px' }}
              placeholder='Search by name or ID'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search fontSize='small' />
                    </InputAdornment>
                  ),
                },
                htmlInput: { 'aria-label': 'Search offers' },
              }}
            />
            <OfferTypeFilter offers={ofKind} value={types} onChange={setTypes} label='Offer types in the library' />
          </Stack>
          {kinds.length > 1 && (
            <Stack
              direction='row'
              useFlexGap
              spacing={0.5}
              sx={{ flexWrap: 'wrap' }}
              role='group'
              aria-label='Offer kind'
            >
              {LIBRARY_KINDS.filter((k) => kinds.includes(k.value)).map((k) => (
                <Chip
                  key={k.value}
                  size='small'
                  label={`${k.label} ${counts[k.value] ?? 0}`}
                  color={kind === k.value ? 'primary' : 'default'}
                  variant={kind === k.value ? 'filled' : 'outlined'}
                  onClick={() => setKind(k.value)}
                  aria-pressed={kind === k.value}
                />
              ))}
            </Stack>
          )}
        </Stack>
      }
      bodyRef={bodyRef}
      bodyProps={{ style: { overflow: 'hidden' } }}
    >
      {loading ? (
        <Stack spacing={1} sx={{ alignItems: 'center', py: 4, color: 'text.secondary' }}>
          <CircularProgress size={24} />
          <Typography variant='body2'>Loading offers</Typography>
        </Stack>
      ) : listed.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 4 }}>
          {deferredQuery ? 'No offers match that search.' : 'Nothing of this kind yet.'}
        </Typography>
      ) : (
        <Droppable
          droppableId={LIBRARY_DROPPABLE_ID}
          mode='virtual'
          isDropDisabled
          renderClone={(provided, snapshot, rubric) => {
            const offer = listed[rubric.source.index];
            return offer ? (
              <LibraryRow offer={offer} provided={provided} dragging={snapshot.isDragging} />
            ) : (
              <div ref={provided.innerRef} />
            );
          }}
        >
          {(provided) => (
            <FixedSizeList
              ref={listRef}
              height={height}
              width='100%'
              itemCount={listed.length}
              itemSize={ROW_HEIGHT}
              itemData={listed}
              overscanCount={6}
              outerRef={provided.innerRef}
            >
              {Row}
            </FixedSizeList>
          )}
        </Droppable>
      )}
    </BuilderPanel>
  );
}
